import { z } from 'zod';
import {
  ConversationMessageContentType,
  conversationGroupMessageScheme,
  conversationMessageScheme,
} from '@/models/conversations/conversation';
import { avatarUrlSchema } from '@/utils/schemas';

export const conversationTypeScheme = z.enum(['session', 'group']);

export type ConversationType = z.infer<typeof conversationTypeScheme>;

export const conversationSessionScheme = z.object({
  ID: z.number(),
  UserID: z.number(),
  HeadImg: avatarUrlSchema,
  NickName: z.string(),
  LastMsgID: z.number().default(0),
  LastMsg: z.string().default(''),
  LastMsgTimeStamp: z.number().default(0),
  LastMsgType: z
    .nativeEnum(ConversationMessageContentType)
    .nullish()
    .default('txt' as ConversationMessageContentType),
  Status: z.coerce.boolean().default(false),
  UnReadCount: z.number().default(0),
  UnReadMaxID: z.number().default(0),
  SessionType: z.literal(1).transform(() => 'session' as const),
  IsTop: z.coerce.boolean().default(false),
  IsNotDisturb: z.coerce.boolean().default(false),
  IsNewSession: z.coerce.boolean().default(false),
  Chats: z.array(conversationMessageScheme).default([]),
});

export const conversationGroupScheme = z.object({
  ID: z.number(),
  GroupImg: avatarUrlSchema,
  GroupName: z.string(),
  GroupType: z.literal(0).or(z.literal(1)).optional().default(0),
  UserID: z.number().optional(),
  UserCount: z.number().default(0),
  LastMsgID: z.number().default(0),
  LastMsg: z.string().default(''),
  LastMsgTimeStamp: z.number().default(0),
  LastMsgType: z
    .nativeEnum(ConversationMessageContentType)
    .nullish()
    .default('txt' as ConversationMessageContentType),
  UnReadCount: z.number().default(0),
  UnReadMaxID: z.number().default(0),
  SessionType: z.literal(2).transform(() => 'group' as const),
  IsTop: z.coerce.boolean().default(false),
  IsNotDisturb: z.coerce.boolean().default(false),
  IsNewSession: z.coerce.boolean().default(false),
  GroupJoinApplyCount: z.number().default(0),
  ReadGroupChatID: z.number().default(0),
  GotAt: z.coerce.boolean().default(false),
  GroupCode: z.number().default(0),
  Chats: z.array(conversationGroupMessageScheme).default([]),
});

const sessionListSessionSchema = conversationSessionScheme.pick({
  ID: true,
  SessionType: true,
  Status: true,
  UserID: true,
  HeadImg: true,
  IsTop: true,
  IsNotDisturb: true,
  IsNewSession: true,
  LastMsgID: true,
  LastMsgTimeStamp: true,
  LastMsg: true,
  LastMsgType: true,
  NickName: true,
});

const sessionListGroupSchema = conversationGroupScheme.pick({
  ID: true,
  SessionType: true,
  UserID: true,
  UserCount: true,
  GroupName: true,
  GroupCode: true,
  GroupType: true,
  GroupJoinApplyCount: true,
  GroupImg: true,
  IsTop: true,
  IsNotDisturb: true,
  IsNewSession: true,
  LastMsgID: true,
  LastMsgTimeStamp: true,
  LastMsg: true,
  LastMsgType: true,
  ReadGroupChatID: true,
});

export const sessionListResponseSchema = z.object({
  ChatSessions: z.array(sessionListSessionSchema),
  Groups: z.array(sessionListGroupSchema),
});

const sessionDataSessionSchema = conversationSessionScheme
  .pick({
    ID: true,
    UserID: true,
    SessionType: true,
    UnReadCount: true,
    UnReadMaxID: true,
    LastMsgID: true,
    LastMsgTimeStamp: true,
    LastMsg: true,
    Chats: true,
  })
  .strip();

const sessionDataGroupSchema = conversationGroupScheme
  .pick({
    ID: true,
    UserID: true,
    SessionType: true,
    UnReadCount: true,
    UnReadMaxID: true,
    LastMsgID: true,
    LastMsgTimeStamp: true,
    LastMsg: true,
    GotAt: true,
    Chats: true,
  })
  .strip();

export const sessionDataResponseSchema = z.object({
  ChatSessions: z.array(sessionDataSessionSchema),
  Groups: z.array(sessionDataGroupSchema),
});

export type SessionDataSessionType = z.infer<typeof sessionDataSessionSchema>;
export type SessionDataGroupType = z.infer<typeof sessionDataGroupSchema>;
export type SessionDataTypeUnion = SessionDataSessionType | SessionDataGroupType;

export type SessionListResponseType = z.infer<typeof sessionListResponseSchema>;
export type SessionDataResponseType = z.infer<typeof sessionDataResponseSchema>;

export type ConversationSessionType = z.infer<typeof conversationSessionScheme>;
export type ConversationGroupType = z.infer<typeof conversationGroupScheme>;

const conversationSessionsTypeUnionScheme = z.discriminatedUnion('SessionType', [
  conversationSessionScheme,
  conversationGroupScheme,
]);

export type ConversationSessionsTypeUnion = z.infer<typeof conversationSessionsTypeUnionScheme>;

export const conversationSessionSettingScheme = z.object({
  ChatSessionID: z.number().nullish(),
  GroupID: z.number().nullish(),
  Name: z.string(),
  IsNotDisturb: z.coerce.boolean(),
  IsTop: z.coerce.boolean(),
  AutoDeleteTime: z.number(),
  ReadDelete: z.coerce.boolean(),
  HeadImg: avatarUrlSchema,
  LastMsg: z.string().default(''),
  LastMsgTimeStamp: z.number().default(0),
  LastTime: z.string().default(''),
});

export type ConversationSessionSettingType = z.infer<typeof conversationSessionSettingScheme>;
